import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import FormularioProducao from '../Formulario/FormularioProducao';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { carregarProducaoDasTarefas, ehResponsavelDaProducao } from '../utils/responsaveisDaProducao';

// Helper: format number to BRL
const formatBRL = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const dataBr = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');

// Shared API call with multiple local fallbacks (pattern used across the project)
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];

  let lastError = null;
  for (const url of urls) {
    try {
      const config = { method, headers: { 'Content-Type': 'application/json' } };
      const token = getAuthToken();
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      if (data && (method === 'POST' || method === 'PUT')) config.body = JSON.stringify(data);
      const res = await fetch(url, config);
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) {
          try { return await res.json(); } catch { return { success: true, status: res.status }; }
        }
        return await res.text();
      } else {
        const errorText = await res.text().catch(() => `Status ${res.status}`);
        lastError = `${url} - ${errorText}`;
        console.error(`Erro na URL ${url}:`, errorText);
      }
    } catch (e) {
      lastError = `${url} - ${e.message}`;
      console.error(`Erro ao conectar com ${url}:`, e);
    }
  }
  throw new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}. Último erro: ${lastError}`);
};

export default function ProducoesDropdown() {
  const navigate = useNavigate();
  const [producoes, setProducoes] = useState([]);
  const [filtradas, setFiltradas] = useState([]);
  const [statusProducao, setStatusProducao] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtro
  const [filtroCampo, setFiltroCampo] = useState('cliente');
  const [filtroValor, setFiltroValor] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);

  // Modal detalhe
  

  // Modal editar
  const [editarAberto, setEditarAberto] = useState(false);
  const [formEditar, setFormEditar] = useState({});
  const [responsaveisProducao, setResponsaveisProducao] = useState([]);
  const [calculatedCosts, setCalculatedCosts] = useState({
    // Previsto (do orçamento)
    orcamentoLaborPrevisto: 0, orcamentoMateriaPrevista: 0, orcamentoServicosTerceiros: 0, orcamentoValorTotal: 0, totalPrevisto: 0,
    // Real (da produção)
    laborReal: 0, materiaReal: 0, servicosTerceirosReal: 0, totalReal: 0
  });
  // Confirmation when missing materials exist for a production before opening edit modal
  const [confirmarFaltantesProducao, setConfirmarFaltantesProducao] = useState({ aberto: false, faltantes: [], producao: null });
  const [pendingProducaoEditar, setPendingProducaoEditar] = useState(null);

  // PDF do aceite final (gerado quando o cliente aprova)
  const [pdfAprovacaoExistente, setPdfAprovacaoExistente] = useState(null);
  const [temFotosAntigas, setTemFotosAntigas] = useState(false);

  useEffect(() => {
    const fetchProducoes = async () => {
      try {
        setLoading(true);
        const data = await apiCall('Producoes');
        const arr = Array.isArray(data) ? data : [];

        // Buscar responsáveis (que agora contêm as horas diretamente no modelo)
        let responsaveisArr = [];
        let producaoDaTarefa = {};
        try {
          const respData = await apiCall('Responsaveis');
          responsaveisArr = Array.isArray(respData) ? respData : [];
          producaoDaTarefa = await carregarProducaoDasTarefas(apiCall);
        } catch (innerErr) {
          console.warn('Não foi possível carregar responsáveis (usando zeros):', innerErr);
          responsaveisArr = [];
        }

        // Enriquecer cada produção com os totais agregados de horas (previstas e reais)
        const enriched = arr.map(p => {
          const producaoId = p.ProducaoId || p.producaoId;

          // ids dos responsáveis vinculados a esta produção
          const responsaveisVinculados = responsaveisArr
            .filter(r => ehResponsavelDaProducao(r, producaoId, producaoDaTarefa));

          // As horas agora vêm direto de cada responsável (novo modelo)
          const totalPrevistas = responsaveisVinculados.reduce((s, r) => s + (parseFloat(r.HorasPrevistas || r.horasPrevistas) || 0), 0);
          const totalReais = responsaveisVinculados.reduce((s, r) => s + (parseFloat(r.HorasReais || r.horasReais) || 0), 0);

          return {
            ...p,
            tarefasHorasPrevistasTotal: totalPrevistas,
            tarefasHorasReaisTotal: totalReais
          };
        });

        setProducoes(enriched);
        setFiltradas(enriched);
        setError('');
      } catch (e) {
        setError('Erro ao carregar projetos. Verifique o backend.');
        setProducoes([]);
        setFiltradas([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducoes();
    carregarStatusProducao();
  }, []);


  const carregarStatusProducao = async () => {
    try {
      const data = await apiCall('StatusProducao');
      setStatusProducao(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar status de projeto:', err);
      setStatusProducao([]);
    }
  };

  useEffect(() => {
    if (!filtroValor.trim()) { setFiltradas(producoes); return; }
    const val = filtroValor.toLowerCase();
    const f = producoes.filter(p => {
      const by = (k) => (p[k] ?? '').toString().toLowerCase();
      if (filtroCampo === 'producao') return (p.ProducaoId || p.producaoId || '').toString().toLowerCase().includes(val);
      if (filtroCampo === 'cliente') return (p.ClienteNome || p.clienteNome || '').toLowerCase().includes(val);
      if (filtroCampo === 'status') return (p.Status || p.status || '').toLowerCase().includes(val);
      if (filtroCampo === 'statusCusto') return (p.Status_custo || p.status_custo || '').toLowerCase().includes(val);
      if (filtroCampo === 'titulo') return (p.Titulo || p.titulo || '').toLowerCase().includes(val);
      if (filtroCampo === 'contrato') return (p.ContratosId || p.contratosId || '').toString().toLowerCase().includes(val);
      return true;
    });
    setFiltradas(f);
  }, [filtroValor, filtroCampo, producoes]);

  const corStatusCusto = (statusCusto) => {
    const s = (statusCusto || '').toString().toLowerCase();
    if (s.includes('dentro')) return { bg: '#e6f4ea', text: '#1e8e3e' };
    if (s.includes('risco')) return { bg: '#fff8e1', text: '#b26a00' };
    if (s.includes('finalizado')) return { bg: '#fdecea', text: '#b00020' };
    if (s.includes('estourado')) return { bg: '#fdecea', text: '#d93025' };
    return { bg: '#eceff1', text: '#455a64' };
  };

  

  const abrirEditar = async (p, skipFaltantesCheck = false) => {
    // Before loading edit, check if this production has MateriaPrimaFaltante entries
    try {
      setLoading(true);
      setFormEditar({
        producaoId: p.ProducaoId || p.producaoId,
        titulo: p.Titulo || p.titulo,
        status: p.Status || p.status,
        statusProducaoId: p.StatusProducaoId || p.statusProducaoId,
        statusCusto: p.Status_custo || p.status_custo,
        statusOriginal: p.Status || p.status,
        dataConclusaoDesenvolvimentoPrevista: p.dataConclusaoDesenvolvimentoPrevista || '',
        dataConclusaoDesenvolvimentoReal: p.dataConclusaoDesenvolvimentoReal || '',
        dataFinalizacaoPrevista: p.dataFinalizacaoPrevista || '',
        dataFinalizacaoReal: p.dataFinalizacaoReal || '',
        garantiaAte: p.garantiaAte || '',
        prazoGarantiaMeses: p.prazoGarantiaMeses,
        contratosId: p.ContratosId || p.contratosId,
        valorContrato: p.ValorContrato || p.valorContrato,
        orcamentoId: p.OrcamentoId || p.orcamentoId,
        orcamentoValor: p.OrcamentoValor || p.orcamentoValor,
        custoOrcado: p.Custo_Orcado || p.custo_Orcado,
        custoAcumulado: p.Custo_Acumulado || p.custo_Acumulado,
        clienteId: p.ClienteId || p.clienteId,
        oportunidadeId: p.OportunidadeId || p.oportunidadeId
      });

      if (!skipFaltantesCheck) {
        const data = await apiCall('MateriaPrimaFaltante');
        const arr = Array.isArray(data) ? data : [];
        const falt = arr.filter(f => (f.producaoId || f.ProducaoId || f.producaoID || f.ProducaoID) == (p.ProducaoId || p.producaoId));
        if (falt && falt.length > 0) {
          setPendingProducaoEditar(p);
          setConfirmarFaltantesProducao({ aberto: true, faltantes: falt, producao: p });
          setLoading(false);
          return;
        }
      }

      // Load responsáveis for this production
      const producaoId = p.ProducaoId || p.producaoId;
      const orcamentoId = p.OrcamentoId || p.orcamentoId;
      try {
        const respData = await apiCall('Responsaveis');
        const producaoDaTarefa = await carregarProducaoDasTarefas(apiCall);
        const respArr = Array.isArray(respData) ? respData.filter(r => ehResponsavelDaProducao(r, producaoId, producaoDaTarefa)) : [];

        // Enrich responsáveis with computed hours from HorasPrevistas/HorasReais fields
        const enriched = respArr.map(r => ({
          ...r,
          nome: r.pessoaNome || r.PessoaNome || r.nome || r.Nome,
          custoHora: r.pessoaCustoHora || r.PessoaCustoHora || r.custoHora || r.CustoHora || 0,
          horasPrevistas: Number(r.horasPrevistas || r.HorasPrevistas || 0) || 0,
          horasReais: Number(r.horasReais || r.HorasReais || 0) || 0
        }));

        setResponsaveisProducao(enriched);

        // === REAL (da produção) ===
        const laborReal = enriched.reduce((s, r) => s + ((r.horasReais || 0) * (r.custoHora || 0)), 0);

        let materiaReal = 0;
        let servicosTerceirosReal = 0;

        try {
          const mpList = await apiCall(`MateriaPrima/por-producao/${producaoId}`);
          const arrMP = Array.isArray(mpList) ? mpList : [];
          materiaReal = arrMP.reduce((sum, m) => {
            return sum + (Number(m.Valor ?? m.valor ?? 0) || 0);
          }, 0);
        } catch (e) {
          console.warn('Erro ao carregar MateriaPrima:', e);
        }

        try {
          const servAll = await apiCall('ServicosTerceiros');
          const svcArr = Array.isArray(servAll) ? servAll : [];
          const matched = svcArr.filter(s => (s.producaoId || s.ProducaoId) == producaoId && (s.orcamentoId ?? s.OrcamentoId) == null); // real: do projeto, sem orçamento
          servicosTerceirosReal = matched.reduce((sum, s) => sum + (Number(s.valor ?? s.Valor ?? 0) || 0), 0);
        } catch (e) {
          console.warn('Erro ao carregar ServicosTerceiros:', e);
        }

        const totalReal = laborReal + materiaReal + servicosTerceirosReal;

        // === PREVISTO (do orçamento) ===
        let orcamentoLaborPrevisto = 0;
        let orcamentoMateriaPrevista = 0;
        let orcamentoServicosTerceiros = 0;
        let orcamentoValorTotal = 0;

        if (orcamentoId) {
          try {
            // Valor total do orçamento
            const orcData = await apiCall(`Orcamento/${orcamentoId}`);
            orcamentoValorTotal = Number(orcData.preco_final ?? orcData.Preco_final ?? orcData.valor ?? orcData.Valor ?? 0) || 0;

            // Responsáveis do orçamento (usa horasPrevistas × custoHora)
            const respOrc = await apiCall(`Responsaveis/por-orcamento/${orcamentoId}`);
            const arrRespOrc = Array.isArray(respOrc) ? respOrc : [];
            orcamentoLaborPrevisto = arrRespOrc.reduce((s, r) => {
              const hPrev = Number(r.horasPrevistas || r.HorasPrevistas || 0) || 0;
              const custo = Number(r.pessoaCustoHora || r.PessoaCustoHora || r.custoHora || r.CustoHora || 0) || 0;
              return s + (hPrev * custo);
            }, 0);

            // Matéria-prima do orçamento
            const mpOrc = await apiCall(`MateriaPrima/por-orcamento/${orcamentoId}`);
            const arrMpOrc = Array.isArray(mpOrc) ? mpOrc : [];
            orcamentoMateriaPrevista = arrMpOrc.reduce((sum, m) => {
              return sum + (Number(m.Valor ?? m.valor ?? 0) || 0);
            }, 0);
          } catch (e) {
            console.warn('Erro ao carregar dados do orçamento para previsto:', e);
          }

          // Serviços terceiros do orçamento
          try {
            const stOrc = await apiCall(`ServicosTerceiros/orcamento/${orcamentoId}`);
            const arrStOrc = Array.isArray(stOrc) ? stOrc : [];
            orcamentoServicosTerceiros = arrStOrc.filter(s => (s.producaoId ?? s.ProducaoId) == null).reduce((sum, s) => sum + (Number(s.valor ?? s.Valor ?? 0) || 0), 0);
          } catch (e) {
            console.warn('Erro ao carregar ServicosTerceiros do orçamento:', e);
          }
        }

        const totalPrevisto = orcamentoValorTotal || (orcamentoLaborPrevisto + orcamentoMateriaPrevista + orcamentoServicosTerceiros);

        setCalculatedCosts({
          orcamentoLaborPrevisto, orcamentoMateriaPrevista, orcamentoServicosTerceiros, orcamentoValorTotal, totalPrevisto,
          laborReal, materiaReal, servicosTerceirosReal, totalReal
        });
      } catch (err) {
        console.error('Erro ao carregar responsáveis:', err);
        setResponsaveisProducao([]);
      }

      // Load PDF if exists
      try {
        await carregarPdfAprovacao(producaoId);
      } catch (e) {
        console.warn('Erro ao carregar PDF:', e);
      }

      try {
        const fotos = await apiCall(`Producoes/${producaoId}/fotos-entrega`);
        setTemFotosAntigas(Array.isArray(fotos) && fotos.length > 0);
      } catch (e) {
        setTemFotosAntigas(false);
      }
    } catch (err) {
      console.error('Erro em abrirEditar:', err);
    } finally {
      setLoading(false);
      setEditarAberto(true);
    }
  };

  const confirmarAbrirEditar = async (answer) => {
    // answer: 'sim' means the user wants to allocate faltantes (navigate to Materia Prima allocation),
    // 'nao' means proceed to open edit modal
    if (!pendingProducaoEditar) {
      setConfirmarFaltantesProducao({ aberto: false, faltantes: [], producao: null });
      return;
    }
    const p = pendingProducaoEditar;
    setConfirmarFaltantesProducao({ aberto: false, faltantes: [], producao: null });
    setPendingProducaoEditar(null);
    if (answer === 'sim') {
      // Navigate to materia-prima page with query param to trigger allocation flow
      try {
        const id = p.ProducaoId || p.producaoId;
        // Navigate to Materia Prima dropdown route with query params to filter by production ID and faltante
        navigate(`/sidebar/MateriaPrimaDropdown?filtroCampo=producao&filtroValor=${encodeURIComponent(id)}&faltante=sim`);
        return;
      } catch (e) {
        console.error('Erro ao navegar para Materia-Prima:', e);
      }
    }
    // If 'nao', open edit modal now (reuse abrirEditar logic) — call load sequence again
    await abrirEditar(p, true);
  };

  const salvarEdicao = async () => {
    try {
      setLoading(true);
      const resposta = await fetch(`${BASE_URL}/Producoes/${formEditar.producaoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Id: parseInt(formEditar.producaoId),
          Titulo: formEditar.titulo || null,
          StatusProducaoId: formEditar.statusProducaoId ? parseInt(formEditar.statusProducaoId) : null,
          DataConclusaoDesenvolvimentoPrevista: formEditar.dataConclusaoDesenvolvimentoPrevista || null,
          DataConclusaoDesenvolvimentoReal: formEditar.dataConclusaoDesenvolvimentoReal || null,
          DataFinalizacaoPrevista: formEditar.dataFinalizacaoPrevista || null,
          ContratosId: formEditar.contratosId ? parseInt(formEditar.contratosId) : null,
          OrcamentoId: formEditar.orcamentoId ? parseInt(formEditar.orcamentoId) : null,
          ClienteId: formEditar.clienteId ? parseInt(formEditar.clienteId) : null,
          OportunidadeId: formEditar.oportunidadeId ? parseInt(formEditar.oportunidadeId) : null
        })
      });

      if (!resposta.ok) {
        const texto = await resposta.text();
        let mensagem = texto;
        try { mensagem = JSON.parse(texto).mensagem || texto; } catch { /* texto simples */ }
        throw new Error(mensagem || `Erro ${resposta.status}`);
      }

      alert('Projeto atualizado com sucesso!');
      setEditarAberto(false);

      // Recarregar lista
      const data = await apiCall('Producoes');
      const arr = Array.isArray(data) ? data : [];
      setProducoes(arr);
      setFiltradas(arr);
    } catch (err) {
      console.error('Erro ao atualizar projeto:', err);
      alert('Erro ao atualizar projeto: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const carregarPdfAprovacao = async (producaoId) => {
    try {
      const baseUrls = [BASE_URL.replace(/\/api\/?$/, '')];

      for (const baseUrl of baseUrls) {
        try {
          const response = await fetch(`${baseUrl}/api/Producoes/${producaoId}/aprovacao-pdf`);
          if (response.ok) {
            const blob = await response.blob();
            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = `aprovacao_${producaoId}.pdf`;
            
            if (contentDisposition) {
              const match = contentDisposition.match(/filename="?(.+)"?/);
              if (match) fileName = match[1];
            }

            setPdfAprovacaoExistente({ blob, fileName, producaoId });
            console.log('✅ PDF de aprovação carregado:', fileName);
            return;
          }
        } catch (err) {
          console.log(`Tentando próxima URL para carregar PDF...`);
        }
      }
      
      // Se chegou aqui, não encontrou PDF
      setPdfAprovacaoExistente(null);
    } catch (err) {
      console.error('Erro ao carregar PDF de aprovação:', err);
      setPdfAprovacaoExistente(null);
    }
  };

  const baixarPdfAprovacao = () => {
    if (!pdfAprovacaoExistente) return;
    
    const url = URL.createObjectURL(pdfAprovacaoExistente.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = pdfAprovacaoExistente.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const Campo = ({ label, value, money = false }) => (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
      <label style={{ fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <span>{money ? formatBRL(value) : (value ?? '—')}</span>
    </div>
  );

  return (
    <div className="container-geral fundo-branco" style={{ minHeight: 'calc(100vh - 60px)', background: '#f0f0f0', padding: 20 }}>
      {/* Filtro */}
      <div className="filtro-container">
        <div className="filtro-wrapper">
          <input
            type="text"
            placeholder={`Filtrar por ${
              filtroCampo === 'producao' ? 'ID Projeto' :
              filtroCampo === 'cliente' ? 'Cliente' :
              filtroCampo === 'status' ? 'Status' :
              filtroCampo === 'statusCusto' ? 'Status de Custo' :
              filtroCampo === 'titulo' ? 'Título' :
              filtroCampo === 'contrato' ? 'Contrato Id' : '—'
            }...`}
            value={filtroValor}
            onChange={(e) => setFiltroValor(e.target.value)}
            className="filtro-input"
          />
          <div className="dropdown-wrapper">
            <button className="dropdown-button" onClick={() => setDropdownAberto(!dropdownAberto)}>
              {filtroCampo === 'producao' ? 'ID Projeto' :
               filtroCampo === 'cliente' ? 'Cliente' :
               filtroCampo === 'status' ? 'Status' :
               filtroCampo === 'statusCusto' ? 'Status de Custo' :
               filtroCampo === 'titulo' ? 'Título' :
               filtroCampo === 'contrato' ? 'Contrato Id' : 'Filtrar por'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {dropdownAberto && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => { setFiltroCampo('producao'); setDropdownAberto(false); }}>Projeto ID</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('cliente'); setDropdownAberto(false); }}>Cliente</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('status'); setDropdownAberto(false); }}>Status</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('statusCusto'); setDropdownAberto(false); }}>Status de Custo</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('titulo'); setDropdownAberto(false); }}>Título</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('contrato'); setDropdownAberto(false); }}>Contrato Id</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Estados */}
      {loading && (<div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando projetos...</div>)}
      {error && (
        <div style={{ textAlign: 'center', padding: 20, color: '#b00020', background: '#fdecea', borderRadius: 6, margin: '20px 0' }}>{error}</div>
      )}
      {/* Confirmation modal for faltantes when opening production edit */}
      {confirmarFaltantesProducao.aberto && (
        <div className="modal-overlay" onClick={() => setConfirmarFaltantesProducao({ aberto: false, faltantes: [], producao: null })} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 620, background: '#fff', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>⚠️ Materiais em falta</h3>
            </div>
            <div style={{ marginBottom: 18 }}>Este projeto possui materiais em falta. Deseja alocar os materiais faltantes agora?</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 12 }}>
              {confirmarFaltantesProducao.faltantes.slice(0,5).map((f, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: 8, border: '1px solid #eee', borderRadius: 6 }}>
                  <div style={{ fontWeight: 700 }}>{f.Descricao || f.descricao || f.EstoqueMaterial?.Titulo || f.EstoqueMaterial?.titulo || 'Material'}</div>
                  <div style={{ color: '#b00020', fontWeight: 700 }}>{f.QuantidadeFaltante ?? f.quantidadeFaltante ?? f.Quantidade ?? '—'}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button style={{ padding: '10px 18px', borderRadius: 6, border: 'none', cursor: 'pointer' }} onClick={() => confirmarAbrirEditar('nao')}>Não</button>
              <button style={{ padding: '10px 18px', borderRadius: 6, backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', cursor: 'pointer' }} onClick={() => confirmarAbrirEditar('sim')}>Sim</button>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      {!loading && !error && (
        <div className="oportunidade-cards">
          {(!filtradas || filtradas.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 10 }}>
              {filtroValor ? 'Nenhum projeto encontrado para o filtro' : 'Nenhum projeto encontrado'}
            </div>
          ) : (
            filtradas.map((p, idx) => {
              const custoStatus = corStatusCusto(p.Status_custo || p.status_custo);
              return (
                <div className="card-cliente" key={p.ProducaoId || p.producaoId || idx} style={{ display: 'flex', borderBottom: '1px solid #000', padding: '10px 0' }}>
                  <div style={{ width: '80%', display: 'flex', flexDirection: 'row', gap: 20, padding: '10px 0', flexWrap: 'wrap' }}>
                    <Campo label="ID Projeto" value={p.ProducaoId || p.producaoId} />
                    <Campo label="Título" value={p.Titulo || p.titulo} />
                    <Campo label="Cliente" value={p.ClienteNome || p.clienteNome} />
                    <Campo label="Contrato Id" value={p.ContratosId || p.contratosId} />
                    <Campo label="Orçamento (R$)" value={p.OrcamentoValor || p.orcamentoValor} money />
                    <Campo label="Custo Orçado (R$)" value={p.Custo_Orcado || p.custo_Orcado} money />
                    <Campo label="Custo Acumulado (R$)" value={p.Custo_Acumulado || p.custo_Acumulado} money />
                    <Campo label="Tarefas Hrs Previstas" value={p.tarefasHorasPrevistasTotal !== undefined ? (p.tarefasHorasPrevistasTotal.toFixed ? p.tarefasHorasPrevistasTotal.toFixed(1) + 'h' : p.tarefasHorasPrevistasTotal + 'h') : '0h'} />
                    <Campo label="Tarefas Hrs Reais" value={p.tarefasHorasReaisTotal !== undefined ? (p.tarefasHorasReaisTotal.toFixed ? p.tarefasHorasReaisTotal.toFixed(1) + 'h' : p.tarefasHorasReaisTotal + 'h') : '0h'} />
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
                      <label style={{ fontWeight: 600, marginBottom: 4 }}>Status de Custo</label>
                      <span style={{ padding: '4px 8px', borderRadius: 12, background: custoStatus.bg, color: custoStatus.text, fontWeight: 600 }}>
                        {p.Status_custo || p.status_custo || '—'}
                      </span>
                    </div>
                    {/* Status do projeto: verde (pendente, finalizado...) e vermelho no Cancelado */}
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
                      <label style={{ fontWeight: 600, marginBottom: 4 }}>Status</label>
                      <span style={{
                        padding: '4px 8px', borderRadius: 12, fontWeight: 600,
                        background: /cancel/i.test(p.Status || p.status || '') ? '#fdecea' : '#e6f4ea',
                        color: /cancel/i.test(p.Status || p.status || '') ? '#d93025' : '#1e8e3e'
                      }}>
                        {p.Status || p.status || '—'}
                      </span>
                    </div>
                    <Campo label="Desenvolvimento (previsto)" value={dataBr(p.dataConclusaoDesenvolvimentoPrevista)} />
                    <Campo label="Desenvolvimento (real)" value={dataBr(p.dataConclusaoDesenvolvimentoReal)} />
                    <Campo label="Finalização (prevista)" value={dataBr(p.dataFinalizacaoPrevista)} />
                    <Campo label="Finalização (real)" value={dataBr(p.dataFinalizacaoReal)} />
                  </div>
                  <div style={{ width: '20%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', gap: 10 }}>
                    <button className="botao-editar" onClick={() => abrirEditar(p)} title="Editar projeto">Editar</button>
                    {/* Detalhes removidos - manter apenas Editar */}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Detalhes removidos conforme solicitado */}

      {/* Modal Editar - SÓ RENDERIZA SE NÃO HOUVER MODAL DE FALTANTES */}
      {!confirmarFaltantesProducao.aberto && (
        <FormularioProducao
          aberto={editarAberto}
          onFechar={() => setEditarAberto(false)}
          form={formEditar}
          setForm={setFormEditar}
          statusProducao={statusProducao}
          responsaveisProducao={responsaveisProducao}
          calculatedCosts={calculatedCosts}
          onSalvar={salvarEdicao}
          onBaixarPdf={baixarPdfAprovacao}
          pdfAprovacaoExistente={pdfAprovacaoExistente}
          temFotosAntigas={temFotosAntigas}
          producaoId={formEditar.producaoId}
          onGestaoFinanceira={() => {
            setEditarAberto(false);
            navigate(`/sidebar/GestaoFinanceiraDropdown?producaoId=${formEditar.producaoId}`);
          }}
          onMateriaPrima={() => {
            setEditarAberto(false);
            navigate(`/sidebar/MateriaPrimaDropdown?filtroCampo=producao&filtroValor=${formEditar.producaoId}`);
          }}
        />
      )}

      {/* Loading Overlay */}
      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
